import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { initializeAuth } from './redux/authSlice';
import './App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Provider, useDispatch } from 'react-redux';
import store from './redux/store';
import GlobalSpinner from './components/GlobalSpinner';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import GlobalError from './components/GlobalError';
import { useLocation } from 'react-router-dom';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Importar páginas y componentes
import LoginPage from './pages/LoginPage';
import NoticiasPage from './pages/NoticiasPage';
import DirectivaSindicatoPage from './pages/DirectivaSindicatoPage';
import Layout from './components/Layout';
import CambioContrasenaPage from './pages/CambioContrasenaPage';
import Home from './pages/Home';
import DocumentosPage from './pages/DocumentosPage';
import ChangePassword from './components/Profile/ChangePassword';
import ConfiguracionPage from './pages/ConfiguracionPage';
import NotFoundPage from './pages/NotFoundPage';
import RencuestaPage from './pages/socios/RencuestaPage';
import BeneficiosPagePublic from './pages/BeneficiosPagePublic';
import ConveniosPagePub from './pages/ConveniosPagePub';
import RecoverPasswordComponent from './components/Auth/RecoverPasswordComponent';
import ResetPasswordComponent from './components/Auth/ResetPasswordComponent';
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CargaMasivaUsuariosPage from './pages/admin/CargaMasivaUsuariosPage';
import ExportarUsuariosPage from './pages/admin/ExportarUsuariosPage';
import ForceDataCompletionModal from './components/ForceDataCompletionModal';
import FAQPage from './pages/FAQPage';

// IMPORT PANEL ADMIN PAGES
import UsuariosPage from './pages/admin/UsuariosPage';
import NoticiasAdminPage from './pages/admin/NoticiasAdminPage';
import DocumentosAdminPage from './pages/admin/DocumentosAdminPage';
import BeneficiosPage from './pages/admin/BeneficiosPage';
import EncuestasPage from './pages/admin/EncuestasPage';
import CorreoMasivoPage from './pages/admin/CorreoMasivoPage';
import DashboardPage from './pages/admin/DashboardPage';
import ProtectedRoute from './components/ProtectedRoute';
import ProfilePage from './pages/ProfilePage';
import ConveniosPage from './pages/admin/ConveniosPage';
import DirectivaAdminPage from './pages/admin/DirectivaAdminPage';
import ResultadosEncuestaPage from './pages/admin/ResultadosEncuestaPage';
import FAQAdminPage from './pages/admin/FAQAdminPage';

// Configuración de íconos FontAwesome
library.add(fab);


function AutoFocus() {
  const location = useLocation();

  useEffect(() => {
    // Mover el foco al contenido principal después de cada cambio de ruta
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }, [location.pathname]);

  return null;
}

function App() {
  const dispatch = useDispatch();
  const isLoading = useSelector(state => state.loading.isLoading);

  // Llama a initializeAuth al cargar la aplicación
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  return (
    <Provider store={store}>
      <Router aria-label="Navegación principal de la aplicación">
        <GlobalError role="alert" aria-live="assertive" aria-atomic="true" />
        <GlobalSpinner aria-live="assertive" aria-busy={isLoading} />
        <ForceDataCompletionModal
          role="dialog"
          aria-labelledby="forceDataCompletionTitle"
          aria-modal="true"
        />

        <AutoFocus />

        <Routes>
          <Route
            path="/"
            element={<Layout role="main" aria-label="Contenido principal" />}
          >
            {/* Rutas públicas */}
            <Route
              index
              element={<Home role="article" aria-label="Página de inicio" />}
            />
            <Route
              path="noticias"
              element={
                <NoticiasPage role="region" aria-label="Sección de noticias" />
              }
            />
            <Route
              path="/preguntas-frecuentes"
              element={
                <FAQPage
                  role="Preguntas Frecuentes"
                  aria-label="Sección de Preguntas Frecuentes Socios"
                />
              }
            />
            <Route
              path="directiva-sindicato"
              element={
                <DirectivaSindicatoPage
                  role="region"
                  aria-label="Directiva del sindicato"
                />
              }
            />
            <Route
              path="/login"
              element={
                <ProtectedRoute preventIfAuthenticated aria-live="polite">
                  <LoginPage role="region" aria-label="Inicio de sesión" />
                </ProtectedRoute>
              }
            />
            <Route
              path="documentos"
              element={
                <ProtectedRoute requireAuth>
                  <DocumentosPage
                    role="region"
                    aria-label="Documentos para socios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="beneficios"
              element={
                <BeneficiosPagePublic
                  role="region"
                  aria-label="Beneficios para socios"
                />
              }
            />
            <Route
              path="convenios"
              element={
                <ConveniosPagePub
                  role="region"
                  aria-label="Convenios para socios"
                />
              }
            />
            <Route
              path="/privacidad"
              element={
                <PrivacyPolicyPage
                  role="article"
                  aria-label="Política de privacidad"
                />
              }
            />
            {/* Recuperacion de password routes */}
            <Route
              path="/recover-password"
              element={
                <RecoverPasswordComponent
                  role="region"
                  aria-label="Recuperación de contraseña"
                />
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <ProtectedRoute preventIfAuthenticated aria-live="polite">
                  <ResetPasswordComponent
                    role="region"
                    aria-label="Restablecer contraseña"
                  />
                </ProtectedRoute>
              }
            />

            {/* Rutas protegidas */}
            <Route
              path="/cambio-clave"
              element={
                <ProtectedRoute requireAuth>
                  <CambioContrasenaPage
                    role="region"
                    aria-label="Cambio de contraseña"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="perfil/cambiar-clave"
              element={
                <ChangePassword
                  role="region"
                  aria-label="Cambiar contraseña de perfil"
                />
              }
            />
            <Route
              path="profile"
              element={
                <ProtectedRoute requireAuth>
                  <ProfilePage role="region" aria-label="Perfil de usuario" />
                </ProtectedRoute>
              }
            />
            <Route
              path="configuracion"
              element={
                <ProtectedRoute requireAuth>
                  <ConfiguracionPage
                    role="region"
                    aria-label="Configuración de cuenta"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/usuarios"
              element={
                <ProtectedRoute adminOnly>
                  <UsuariosPage
                    role="region"
                    aria-label="Administración de usuarios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/noticias"
              element={
                <ProtectedRoute adminOnly>
                  <NoticiasAdminPage
                    role="region"
                    aria-label="Administración de noticias"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/documentos"
              element={
                <ProtectedRoute adminOnly>
                  <DocumentosAdminPage
                    role="region"
                    aria-label="Administración de documentos"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/beneficios"
              element={
                <ProtectedRoute adminOnly>
                  <BeneficiosPage
                    role="region"
                    aria-label="Administración de beneficios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/encuestas"
              element={
                <ProtectedRoute adminOnly>
                  <EncuestasPage
                    role="region"
                    aria-label="Administración de encuestas"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/encuestas/resultados"
              element={
                <ProtectedRoute adminOnly>
                  <ResultadosEncuestaPage
                    role="region"
                    aria-label="Resultados de encuestas"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/faq"
              element={
                <ProtectedRoute>
                  <FAQAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/correos"
              element={
                <ProtectedRoute adminOnly>
                  <CorreoMasivoPage
                    role="region"
                    aria-label="Envío de correos masivos"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/dashboard"
              element={
                <ProtectedRoute adminOnly>
                  <DashboardPage
                    role="region"
                    aria-label="Panel de administración"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="encuestas"
              element={
                <ProtectedRoute requireAuth>
                  <RencuestaPage
                    role="region"
                    aria-label="Encuestas para socios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/convenios"
              element={
                <ProtectedRoute adminOnly>
                  <ConveniosPage
                    role="region"
                    aria-label="Administración de convenios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/carga-masiva-usuarios"
              element={
                <ProtectedRoute adminOnly>
                  <CargaMasivaUsuariosPage
                    role="region"
                    aria-label="Carga masiva de usuarios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/exportar-usuarios"
              element={
                <ProtectedRoute adminOnly>
                  <ExportarUsuariosPage
                    role="region"
                    aria-label="Exportación de usuarios"
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/directiva"
              element={
                <ProtectedRoute adminOnly>
                  <DirectivaAdminPage
                    role="region"
                    aria-label="Administración de directiva"
                  />
                </ProtectedRoute>
              }
            />

            {/* Ruta 404 */}
            <Route
              path="*"
              element={<NotFoundPage role="alert" aria-live="assertive" />}
            />
          </Route>
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;